<template>
  <div class="content auth-page">
    <div class="md-layout md-alignment-center-center">
      <div
        class="md-layout-item md-size-50 md-medium-size-70 md-small-size-100"
      >
        <md-card>
          <md-card-header data-background-color="blue">
            <h4 class="title">Acceso al modulo</h4>
            <p class="category">
              Ingresa cohort, commissionId y el dato de acceso correspondiente
            </p>
          </md-card-header>

          <md-card-content>
            <div class="md-layout">
              <div class="md-layout-item md-size-100">
                <md-field>
      
                  <select v-model="form.role" class="access-select">
                    <option value="delegate">Delegado</option>
                    <option value="president">Presidente</option>
                  </select>
                </md-field>
              </div>

              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Cohort</label>
                  <md-input v-model="form.cohort" />
                </md-field>
              </div>

              <div class="md-layout-item md-size-100">
                <md-field>
                  <label>Commission ID</label>
                  <md-input v-model="form.commissionId" />
                </md-field>
              </div>

              <div v-if="form.role === 'delegate'" class="md-layout-item md-size-100">
                <md-field>
                  <label>Delegate ID</label>
                  <md-input v-model="form.delegateId" />
                </md-field>
              </div>

              <div v-else class="md-layout-item md-size-100">
                <md-field>
                  <label>Documento</label>
                  <md-input v-model="form.documento" />
                </md-field>
              </div>
            </div>

            <p v-if="error" class="text-danger">{{ error }}</p>
          </md-card-content>

          <md-card-actions>
            <md-button class="md-raised md-primary" @click="submitAuth">
              Entrar
            </md-button>
          </md-card-actions>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/fb";
import { saveAuthContext } from "@/authContext";
import { loadDelegateProfile } from "@/delegateStore";

export default {
  name: "AuthPage",
  data() {
    return {
      form: {
        role: "delegate",
        cohort: "",
        commissionId: "",
        delegateId: "",
        documento: "",
      },
      error: "",
    };
  },
  mounted() {
    // 1. Unificamos fuentes de datos: URL nativa + Router
    // Tu URL tiene los parámetros antes del '#' (?cohort=...), por lo que el 
    // Router de Vue (en modo hash) no los detecta. Usamos URLSearchParams para capturarlos.
    const nativeParams = new URLSearchParams(window.location.search);
    const routerParams = this.$route.query;

    const getParam = (key) => routerParams[key] || nativeParams.get(key);

    /**
     * 2. Función de limpieza profunda:
     * Elimina fragmentos de hash (#) y también posibles duplicaciones de parámetros 
     * que el router concatena si hay redirecciones múltiples.
     */
    const clean = (val) => {
      if (typeof val !== "string") return val;
      // Quitamos todo lo que venga después de un # o un ? extra
      return val.split('#')[0].split('?')[0];
    };

    const queryContext = {
      cohort: clean(getParam('cohort')),
      commissionId: clean(getParam('commissionId') || getParam('commissionsId') || getParam('commisionId')),
      delegateId: clean(getParam('delegateId')),
      documento: clean(getParam('documento') || getParam('document')),
      role: clean(getParam('role') || getParam('accessType')),
    };

    const inferredRole = String(queryContext.role || (queryContext.documento ? "president" : "delegate")).toLowerCase();

    // 3. Verificamos si tenemos los datos mínimos para el formulario
    const hasBaseData = queryContext.cohort && queryContext.commissionId;
    const hasIdentity = queryContext.delegateId || queryContext.documento;

    if (hasBaseData || hasIdentity) {
      this.form = {
        cohort: queryContext.cohort || "",
        commissionId: queryContext.commissionId || "",
        delegateId: queryContext.delegateId || "",
        documento: queryContext.documento || "",
        role: inferredRole === "president" ? "president" : "delegate",
      };
    }

    // 4. Si la URL trae credenciales completas, limpiamos la URL visualmente y entramos
    if (hasBaseData && hasIdentity) {
      // Limpiamos los parámetros de la barra de direcciones para evitar el "spam" de la app
      // Esto previene que si el usuario refresca, la URL siga sucia.
      if (Object.keys(this.$route.query).length > 0) {
        this.$router.replace({ query: {} }).catch(() => {});
      }
      
      // Limpiamos también la búsqueda nativa (?cohort=...) para dejar la URL impecable
      if (window.location.search) {
        window.history.replaceState({}, document.title, window.location.pathname + window.location.hash);
      }
      
      // Ejecutamos la persistencia
      this.persistAndRedirect({ ...queryContext, role: inferredRole });
    }
  },
  methods: {
    async validatePresidentAccess(rawContext) {
      const commissionRef = doc(
        db,
        "commissions",
        rawContext.cohort,
        "commissions",
        rawContext.commissionId
      );

      const commissionSnapshot = await getDoc(commissionRef);
      if (!commissionSnapshot.exists()) {
        throw new Error("No se encontró la comisión indicada.");
      }

      const commissionData = commissionSnapshot.data() || {};
      const presidents = Array.isArray(commissionData.presidents)
        ? commissionData.presidents
        : [];
      const normalizedDocument = String(rawContext.documento || "").trim();

      const match = presidents.find((president) => {
        const ti = president && (president.ti || president.documento || president.id);
        return String(ti || "").trim() === normalizedDocument;
      });

      if (!match) {
        throw new Error("El documento no está registrado como presidente en esta comisión.");
      }

      return match;
    },
    async persistAndRedirect(rawContext) {
      const role = String(rawContext.role || (rawContext.documento ? "president" : "delegate")).toLowerCase();

      try {
        if (role === "president") {
          await this.validatePresidentAccess(rawContext);
        }

        const context =
          role === "president"
            ? saveAuthContext({
                cohort: rawContext.cohort,
                commissionId: rawContext.commissionId,
                role: "president",
                documento: rawContext.documento,
              })
            : saveAuthContext({
                cohort: rawContext.cohort,
                commissionId: rawContext.commissionId,
                role: "delegate",
                delegateId: rawContext.delegateId,
              });

        if (!context) {
          this.error =
            role === "president"
              ? "Debes completar cohort, commissionId y documento."
              : "Debes completar cohort, commissionId y delegateId.";
          return;
        }

        await loadDelegateProfile({ force: true });

        this.$router.replace("/dashboard");
      } catch (error) {
        this.error =
          error && error.message
            ? error.message
            : role === "president"
              ? "No se pudo validar el acceso del presidente."
              : "No se pudo validar el acceso.";
      }
    },
    async submitAuth() {
      this.error = "";
      await this.persistAndRedirect(this.form);
    },
  },
};
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 70px);
  display: flex;
  align-items: center;
}

.access-select {
  width: 100%;
  min-height: 32px;
  border: 0;
  background: transparent;
  color: inherit;
  outline: none;
}

.md-card-actions {
  justify-content: flex-end;
}
</style>
